import { HubConnectionState } from "@microsoft/signalr";
import { Button } from "@progress/kendo-react-buttons";
import { Loader } from "@progress/kendo-react-indicators";
import { TextArea } from "@progress/kendo-react-inputs";
import { Clock, Cpu, Loader as LoaderIcon, Plus, Send } from "lucide-react";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent, type PropsWithChildren, type SubmitEvent } from "react";
import { useLazyGetChatConversationQuery } from "../api/chatApi";
import { useAppDispatch } from "../app/hooks";
import { ChatMessageType, type ChatMessageDto } from "../contracts";
import type { ChatConversationViewModel } from "../models/chatMessageViewModel";
import { isCurrentMessageToolRequest } from "../models/toolRequestModels";
import ActionButton from "../pages/workspace/components/atoms/actionButton";
import { clearCurrentCommercialPlan } from "../slices/chatSlice";
import { formatRelativeTime, toDate } from "../utils/dateValueConversion";
import type { ChatConversationRuntime } from "./context/chatConversationContext";
import { ChatConversationProvider } from "./context/chatConversationProvider";
import { useChatController, type ConversationChangedOptions } from "./controller/useChatController";
import ToolRenderer from "./tools/toolRenderer";

export type ChatConversationSelectionHandler = (conversationId: number | undefined) => Promise<void>;

export type SimulatorStartedAnimationOptions = {
    conversationId?: number;
    title: string;
    summary: string;
    sourceRect?: DOMRect;
};

export type ChatConversationRef = {
    selectConversationAsync: ChatConversationSelectionHandler;
    startNewConversation: () => void;
};

export type ChatConversationProps = {
    conversationId?: number;
    initialConversation?: ChatConversationViewModel;
    placeholder?: string;
    isCommercialHistoryOpen?: boolean;
    onToggleCommercialHistory?: () => void;
    onConversationChanged?: (conversationId: number | undefined, options?: ConversationChangedOptions) => void;
};

type ChatHeaderProps = {
    hasStartedSimulation?: boolean;
    chatConnected: boolean;
    isFetchingConversation?: boolean;
    isCommercialHistoryOpen?: boolean;
    onToggleCommercialHistory?: () => void;
    handleStartNewConversation?: () => void;
};

const isMacComposer = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.userAgent);

const shouldRenderChatMessage = ({ type }: ChatMessageDto): boolean =>
    [
        ChatMessageType.User,
        ChatMessageType.Assistant,
        ChatMessageType.ToolRequest
    ].includes(type);

type ChatBubbleType = { type?: ChatMessageType };

const ChatBubble = ({ type, children }: PropsWithChildren<ChatBubbleType>) => {
    const USER_BUBBLE_CLASS_NAME = "bg-white border rounded-3 px-3 py-2 text-break min-w-0 text-body";
    const ASSISTANT_BUBBLE_CLASS_NAME = "k-bg-brand-light-teal k-border-brand-ai-surface k-text-brand-teal rounded-3 px-3 py-2 text-break min-w-0 d-inline-flex align-items-center gap-2";
    const TOOL_BUBBLE_CLASS_NAME = "k-bg-brand-light-teal k-text-brand-teal border shadow-sm rounded-3 px-3 py-2 text-break min-w-0";

    const classBase = type === ChatMessageType.User
        ? USER_BUBBLE_CLASS_NAME
        : type === ChatMessageType.ToolRequest ? TOOL_BUBBLE_CLASS_NAME : ASSISTANT_BUBBLE_CLASS_NAME;

    return (
        <div
            className={classBase}
            style={{ maxWidth: "min(90%, 100%)" }}
        >
            {children}
        </div>
    );
};

export const SovraSignalIcon = ({ connected, showPulse = true }: { connected: boolean; showPulse: boolean }) => {
    return (
        <div className="relative flex-shrink-0">
            {/* Pulse ring — only when connected */}
            {connected && showPulse && (
                <span
                    className="position-absolute !k-bg-primary rounded-2 animate-ping"
                    style={{ minWidth: "20px", minHeight: "20px", top: "10%", left: "15%" }}
                />
            )}
            {/* Rounded square body */}
            <svg className={showPulse ? "ms-1 mt-1" : ""} width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect
                    x="1"
                    y="1"
                    width="18"
                    height="18"
                    rx="5"
                    fill={connected ? "var(--brand-teal)" : "none"}
                    stroke={connected ? "var(--brand-teal)" : "#94a3b8"}
                    strokeWidth="1.5"
                />
                {/* Inner dot */}
                <rect
                    x="7.5"
                    y="7.5"
                    width="5"
                    height="5"
                    rx="1.5"
                    fill={connected ? "white" : "#94a3b8"}
                    opacity={connected ? 1 : 0.5}
                />
            </svg>
        </div>
    );
};

const ChatHeader = ({
    hasStartedSimulation,
    chatConnected,
    isFetchingConversation,
    isCommercialHistoryOpen,
    handleStartNewConversation,
    onToggleCommercialHistory
}: ChatHeaderProps) => {
    return (
        <div className="chat-conversation-header d-flex align-items-center justify-content-between m-3 pb-3 border-bottom min-w-0 row-gap-3">
            <div className="d-flex gap-3 items-align-center" style={{ minWidth: "210px" }}>
                <div className="position-relative">
                    <SovraSignalIcon connected={chatConnected} showPulse={false} />
                </div>
                <div className="d-flex flex-column me-2">
                    <h2 className="h6 fw-bold !k-text-primary mb-0">Ask Sovra AI</h2>
                    <p className="fs-8 !k-text-subtle mb-0">Commercial Strategy Advisor</p>
                </div>
            </div>
            <div className="chat-conversation-header-actions d-flex align-items-center gap-1 flex-shrink-0 min-w-0">
                <div
                    className={`d-flex align-items-center gap-2 min-w-0 justify-content-between flex-grow-1 k-bg-brand-light-teal !k-border-brand-ai-surface rounded-3  ${hasStartedSimulation ? "ps-2" : ""}`}
                    style={{ minWidth: hasStartedSimulation ? "265px" : 0 }}
                >
                    {hasStartedSimulation && (
                        <span
                            className="d-inline-flex align-items-center gap-2 k-text-brand-teal fw-semibold fs-8 flex-shrink-0 ps-3"
                            role="status"
                            aria-live="polite"
                        >
                            <span className="icon-rotate-infinite d-inline-flex">
                                <LoaderIcon size={14} strokeWidth={2} className="k-text-brand-teal" />
                            </span>
                            Simulation running
                        </span>
                    )}
                    <ActionButton
                        type="button"
                        variant="highlighted"
                        size="small"
                        aria-label="New chat"
                        disabled={isFetchingConversation}
                        onClick={handleStartNewConversation}
                        startIcon={<Plus size={14} strokeWidth={2} />}
                    >
                        New chat
                    </ActionButton>
                </div>
                {onToggleCommercialHistory && (
                    <ActionButton
                        type="button"
                        variant="plane"
                        size="small"
                        aria-label="Commercial History"
                        aria-expanded={isCommercialHistoryOpen}
                        aria-controls="commercial-history-panel"
                        disabled={isFetchingConversation}
                        onClick={onToggleCommercialHistory}
                        startIcon={<Clock size={14} strokeWidth={2} />}
                    >
                        Commercial History
                    </ActionButton>
                )}
            </div>
        </div>
    );
};

const ChatConversation = forwardRef<ChatConversationRef, ChatConversationProps>((
    {
        conversationId,
        initialConversation,
        placeholder = "Ask a follow-up question...",
        isCommercialHistoryOpen = false,
        onToggleCommercialHistory,
        onConversationChanged
    },
    ref
) => {
    const dispatch = useAppDispatch();
    const chatContainerRef = useRef<HTMLDivElement | null>(null);
    const startSimulationSourceRef = useRef<HTMLFormElement | null>(null);
    const loadedConversationIdRef = useRef<number | undefined>(initialConversation?.id);
    const failedConversationIdRef = useRef<number | undefined>(undefined);

    const [loadConversationAsync, { isFetching: isFetchingConversation }] = useLazyGetChatConversationQuery();
    const [loadConversationError, setLoadConversationError] = useState<string | undefined>();

    const handleControllerConversationChanged = useCallback((changedConversationId: number | undefined, options?: ConversationChangedOptions): void => {
        loadedConversationIdRef.current = changedConversationId;
        onConversationChanged?.(changedConversationId, options);
    }, [onConversationChanged]);

    const {
        chatMessages,
        query,
        setQuery,
        hasError,
        isChatBusy,
        errorMessage,
        clearError,
        connectionState,
        submitQueryAsync,
        actions,
        replaceConversation,
        startNewConversation,
        hasStartedSimulation
    } = useChatController({
        onConversationChanged: handleControllerConversationChanged
    });

    const runtime = useMemo<ChatConversationRuntime>(() => ({
        chatMessages,
        isChatBusy,
        hasStartedSimulation,
        actions
    }), [
        chatMessages,
        isChatBusy,
        hasStartedSimulation,
        actions
    ]);

    const scrollChatToBottom = useCallback((): void => {
        if (!chatContainerRef.current) {
            return;
        }
        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }, []);

    const handleStartNewConversation = useCallback((): void => {
        loadedConversationIdRef.current = undefined;
        failedConversationIdRef.current = undefined;
        startNewConversation();
        setLoadConversationError(undefined);
        dispatch(clearCurrentCommercialPlan());
    }, [dispatch, startNewConversation]);

    const handleSelectConversationAsync = useCallback(async (conversationId: number | undefined): Promise<void> => {
        if (conversationId == null) {
            if (loadedConversationIdRef.current != null) {
                handleStartNewConversation();
            }

            return;
        }

        if (
            conversationId <= 0
            || conversationId === loadedConversationIdRef.current
            || conversationId === failedConversationIdRef.current
            || isFetchingConversation
        ) {
            return;
        }

        failedConversationIdRef.current = undefined;
        setLoadConversationError(undefined);
        clearError();

        try {
            const response = await loadConversationAsync({
                chat_conversation_id: conversationId
            }).unwrap();

            loadedConversationIdRef.current = response.chat_conversation.id;
            failedConversationIdRef.current = undefined;
            replaceConversation(response.chat_conversation);
        } catch (error) {
            failedConversationIdRef.current = conversationId;
            console.error("Error loading simulator chat conversation.", error);
            setLoadConversationError(error instanceof Error ? error.message : "Unable to load conversation. Please try again.");
        }
    }, [clearError, handleStartNewConversation, isFetchingConversation, loadConversationAsync, replaceConversation]);

    useImperativeHandle(ref, () => ({
        selectConversationAsync: handleSelectConversationAsync,
        startNewConversation: handleStartNewConversation
    }), [handleSelectConversationAsync, handleStartNewConversation]);

    useEffect(() => {
        if (conversationId == null) {
            if (loadedConversationIdRef.current == null) {
                return;
            }

            const timeoutId = window.setTimeout(() => {
                handleStartNewConversation();
            }, 0);

            return () => {
                window.clearTimeout(timeoutId);
            };
        }

        if (
            conversationId === loadedConversationIdRef.current
            || conversationId === failedConversationIdRef.current
        ) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            void handleSelectConversationAsync(conversationId);
        }, 0);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [conversationId, handleSelectConversationAsync, handleStartNewConversation]);

    useEffect(() => {
        scrollChatToBottom();
    }, [chatMessages, isChatBusy, hasError, scrollChatToBottom]);

    useEffect(() => {
        if (!hasError) {
            return;
        }

        const timeoutId = window.setTimeout(() => {
            clearError();
        }, 10000);

        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [clearError, hasError, errorMessage]);

    useEffect(() => {
        if (!chatContainerRef.current) {
            return;
        }

        const mutationObserver = new MutationObserver(() => {
            scrollChatToBottom();
        });

        mutationObserver.observe(chatContainerRef.current, {
            childList: true,
            characterData: true,
            subtree: true
        });

        return () => {
            mutationObserver.disconnect();
        };
    }, [scrollChatToBottom]);

    const isToolCallInProgress = isCurrentMessageToolRequest(chatMessages);
    const isInputDisabled = isChatBusy || isFetchingConversation || isToolCallInProgress || connectionState !== HubConnectionState.Connected;
    const canSubmit = Boolean(query.trim()) && !isInputDisabled;
    const inputStyle = isInputDisabled
        ? {
                opacity: 0.65,
                cursor: "not-allowed",
                backgroundColor: "var(--bs-secondary-bg)",
                borderColor: "var(--bs-border-color)",
                boxShadow: "none"
            }
        : undefined;
    const lastUpdatedLabel = useMemo(() => {
        const latestMessage = [...chatMessages].reverse().find(message => toDate(message.created_at));

        return formatRelativeTime(latestMessage?.created_at);
    }, [chatMessages]);

    const handleSubmitQuery = async (event: SubmitEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!canSubmit) {
            return;
        }

        await submitQueryAsync();
    };

    const handleComposerKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
        if (event.key !== "Enter" || !(event.metaKey || event.ctrlKey)) {
            return;
        }

        event.preventDefault();

        if (!canSubmit) {
            return;
        }

        void submitQueryAsync();
    };

    return (
        <ChatConversationProvider value={runtime}>
            <div className="chat-conversation shadow-sm h-100 w-100 min-w-0 d-flex flex-column overflow-hidden">
                <ChatHeader
                    hasStartedSimulation={hasStartedSimulation}
                    chatConnected={connectionState === HubConnectionState.Connected}
                    isFetchingConversation={isFetchingConversation}
                    isCommercialHistoryOpen={isCommercialHistoryOpen}
                    onToggleCommercialHistory={onToggleCommercialHistory}
                    handleStartNewConversation={handleStartNewConversation}
                />

                {(hasError || loadConversationError) && (
                    <div className="alert alert-danger py-2 px-3 mx-3 d-flex justify-content-between align-items-start gap-2 min-w-0" role="alert">
                        <span className="text-break min-w-0">Your request timed out or an error occurred. Please try again.</span>
                        <Button
                            type="button"
                            fillMode="flat"
                            size="small"
                            className="p-0 lh-1 flex-shrink-0"
                            onClick={() => {
                                clearError();
                                setLoadConversationError(undefined);
                            }}
                            aria-label="Dismiss error"
                        >
                            ×
                        </Button>
                    </div>
                )}
                <div
                    className="d-flex flex-column gap-3 px-4 pb-3 flex-grow-1 overflow-auto min-w-0"
                    aria-live="polite"
                    aria-busy={isChatBusy || isFetchingConversation}
                    ref={chatContainerRef}
                >
                    {isFetchingConversation && (
                        <div className="d-flex justify-content-start min-w-0 w-100">
                            <ChatBubble>
                                <Loader type="converging-spinner" size="small" />
                                <div className="min-w-0">
                                    <p className="mb-1 fw-semibold">Loading conversation</p>
                                    <p className="mb-0 small">Fetching the selected conversation.</p>
                                </div>
                            </ChatBubble>
                        </div>
                    )}
                    <p className="text-uppercase fw-semibold !k-text-subtle letter-spacing-wide fs-9 mb-2">
                        Chat
                    </p>
                    {chatMessages.filter(shouldRenderChatMessage).map(message => (
                        <div
                            key={`msg_${message.id ?? message.client_message_id}`}
                            className={`d-flex min-w-0 w-100 ${message.type === ChatMessageType.User ? "justify-content-end" : "justify-content-start"}`}
                        >
                            <ChatBubble type={message.type}>
                                <ToolRenderer message={message} />
                            </ChatBubble>
                        </div>
                    ))}
                    {isChatBusy && (
                        <div className="d-flex justify-content-start min-w-0 w-100">
                            <ChatBubble>
                                <Loader type="converging-spinner" size="small" />
                                <div className="min-w-0">
                                    <p className="mb-1 fw-semibold">Sovra AI is processing your request</p>
                                    <p className="mb-0 small">Analysing your prompt and preparing a response.</p>
                                </div>
                            </ChatBubble>
                        </div>
                    )}

                    <div className="d-flex align-items-center gap-2 py-3 mt-3 border-top fs-8 flex-shrink-0 min-w-0 !k-text-helper">
                        <Cpu size={12} strokeWidth={2} className="flex-shrink-0" />
                        <span className="text-truncate">
                            Powered by
                            {" "}
                            <span className="fw-semibold">Sovra AI Engine</span>
                            {lastUpdatedLabel && ` • Last updated ${lastUpdatedLabel}`}
                        </span>
                    </div>
                </div>

                <form ref={startSimulationSourceRef} onSubmit={handleSubmitQuery} className="m-3 mt-2 flex-shrink-0 min-w-0 pt-3 border-top">
                    <div className="d-flex gap-2 align-items-end min-w-0">
                        <div className="flex-grow-1 min-w-0">
                            <TextArea
                                className="w-100 rounded-3"
                                value={query}
                                placeholder={placeholder}
                                disabled={isInputDisabled}
                                rows={3}
                                style={inputStyle}
                                onChange={event => setQuery(event.value ?? "")}
                                onKeyDown={handleComposerKeyDown}
                            />
                        </div>
                        <ActionButton
                            type="submit"
                            variant="primary"
                            aria-label="Send"
                            disabled={!canSubmit}
                            innerClassName="d-inline-flex align-items-center justify-content-center p-2"
                        >
                            <Send size={16} strokeWidth={2} />
                        </ActionButton>
                    </div>
                    <p className="fs-8 text-muted mb-0 mt-2 d-flex align-items-center gap-1">
                        <kbd className="bg-white text-secondary border rounded px-1 py-0 fw-normal shadow-sm">
                            {isMacComposer ? "⌘" : "Ctrl"}
                        </kbd>
                        <span>+</span>
                        <kbd className="bg-white text-secondary border rounded px-1 py-0 fw-normal shadow-sm">
                            Enter
                        </kbd>
                        <span>to send</span>
                    </p>
                </form>
            </div>
        </ChatConversationProvider>
    );
});

export default ChatConversation;
